import React, { useContext, useEffect, useMemo, useState } from "react";
import { Box, Container, Grid, Paper, Typography } from "@material-ui/core";
import useStyles from "./styles";
import {
  PurchasesKpi,
  RefundsKpi,
  TotalSalesKpi,
  VisitorsKpi
} from "./components/KPI/KPI";
import { TopSellingProducts } from "./components/Products/TopSellingProducts";
import Hidden from "@material-ui/core/Hidden";
import { Sales } from "./components/Diagrams/Sales";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import { AppState } from "../../store";
import {
  calculateTrend,
  statisticsKpiDefault,
  updateDatedSalesStatistics,
  updateRefundsStatistics,
  updateUserEventsKpiStatistics
} from "./utils";
import { Outlet } from "react-router-dom";
import * as StatisticsApi from "../../api/statisticsApi";
import { isErr, isOk } from "../../result";
import { Refund } from "../../models/Statistics";
import { UserEvents } from "./components/UserEvents/UserEvents";
import { statisticsUserEventsDefault } from "./components/UserEvents/types";
import {
  collection,
  limit,
  onSnapshot,
  orderBy,
  query,
  where
} from "firebase/firestore";
import { UserEvent as UserEventModel } from "./types";
import { Authenticated, LifvsUser } from "../../models/User";
import * as DashboardApi from "../../api/dashboardApi";
import {
  thunkFetchStores,
  thunkFetchFilteredStores
} from "../../store/stores/actions";
import { thunkFetchUsers } from "../../store/users/actions";
import { useLoadIfEmpty } from "../../hooks/useLoadIfEmpty";
import { Store } from "../../models/Store";
import {
  firebase,
  logFirestoreError,
  dateAndTenantConstraints,
  sameId
} from "../../firebase";
import { OnBoardingDialog } from "./components/OnBoarding/OnBoardingDialog";
import { UserContext } from "../../UserProvider";
import { MobilePOSPurchases } from "./components/MobilePOS/MobilePOSPurchases";
import { FilteredStoresState, StoresState } from "../../store/stores/types";
import { MobilePOSPurchase } from "../../models/MobilePOS";
import { TenantTypeName } from "../../models/Tenant";
import { useI18n } from "../../hooks/useI18n";

export const Dashboard = () => {
  const classes = useStyles();
  const { t } = useI18n();

  const currentUser = useContext(UserContext) as Authenticated;
  const isMobilePOS =
    currentUser.tenant.tenantType.name === TenantTypeName.MobilePOS;

  const todayNow = useMemo(() => new Date(), []);
  const todayStart = useMemo(
    () => moment(todayNow).startOf("day").toDate(),
    [todayNow]
  );

  const lastWeekThenStart = moment(todayStart).subtract(1, "week").toDate();
  const lastWeekThenEnd = moment(todayNow).subtract(1, "week").toDate();

  const [loading, setLoading] = useState<boolean>(true);

  const weekDayName = lastWeekThenStart.toLocaleString("sv-SE", {
    weekday: "long"
  });

  const storesState = useSelector<AppState, StoresState>(s => s.stores);
  const filteredStoresState = useSelector<AppState, FilteredStoresState>(
    s => s.filteredStores
  );
  const dashboardStores = useMemo(() => {
    if (filteredStoresState?.stores?.length) {
      return filteredStoresState.stores;
    }
    return storesState?.stores || [];
  }, [filteredStoresState, storesState]);

  const usersState =
    useSelector<AppState, ReadonlyArray<LifvsUser>>(s => s.users.users) || [];

  useLoadIfEmpty(!storesState?.stores?.length, thunkFetchStores);
  useLoadIfEmpty(filteredStoresState.isLoading, thunkFetchFilteredStores);
  useLoadIfEmpty(!usersState.length, thunkFetchUsers);

  const [salesKpiData, setSalesKpiData] = useState(statisticsKpiDefault.sales);

  const [userEventsKpiData, setUserEventsKpiData] = useState(
    statisticsKpiDefault.userEvents
  );

  const [userEventsData, setUserEventsData] = useState(
    statisticsUserEventsDefault
  );

  const [mobilePurchasesState, setMobilePurchasesState] = useState<
    MobilePOSPurchase[]
  >([]);

  const [refunds, setRefunds] = useState(statisticsKpiDefault.refunds);

  //TODO CHECK ALL DASHBOARD REDUX STUFF. CAN BE DELETED?

  useEffect(() => {
    if (!isMobilePOS) {
      return;
    }
    const fetchPurchases = async () => {
      setLoading(true);
      try {
        const [response] = await Promise.all([
          DashboardApi.getMobilePOSPurchases(todayStart, todayNow)
        ]);
        if (isOk(response)) {
          const value = response.value as MobilePOSPurchase[];
          setMobilePurchasesState(value);
        }
      } catch (error) {
        console.error("Error fetching purchases:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchPurchases();
  }, [todayStart, todayNow]);

  useEffect(() => {
    if (isMobilePOS) {
      setRefunds({ ...refunds, isLoading: false });
      setUserEventsData({ ...userEventsData, isLoading: false });
      setSalesKpiData({ ...salesKpiData, isLoading: false });
      setUserEventsKpiData({ ...userEventsKpiData, isLoading: false });
      return;
    }

    if (
      !usersState ||
      usersState.length === 0 ||
      dashboardStores.length === 0
    ) {
      setUserEventsData({ ...userEventsData, isLoading: false });
      return;
    }

    setRefunds(statisticsKpiDefault.refunds);
    Promise.all([
      StatisticsApi.getRefunds(
        todayStart,
        todayNow,
        dashboardStores.map(s => s.id)
      )
    ])
      .then(res => {
        const err = res.find(isErr);
        if (err) {
          throw err;
        }

        if (isOk(res[0])) {
          const _refunds = updateRefundsStatistics([res[0].value as Refund[]]);
          setRefunds(_refunds as any);
        }
      })
      .catch(error => {
        setRefunds({ ...statisticsKpiDefault.refunds, isLoading: false });
      });

    const storeids = dashboardStores
      .filter(s => s.state === "Opened")
      .map(x => x.id);

    const tenantIds = [
      ...new Set(dashboardStores.map(s => s.cluster?.id).filter(Boolean))
    ];
    const q = query(
      collection(firebase.firestore, "UserEvent"),
      storeids.length < 10 && storeids.length != 0
        ? where("StoreId", "in", storeids)
        : tenantIds.length > 1 && tenantIds.length <= 10
        ? where("TenantId", "in", tenantIds)
        : where("TenantId", "==", tenantIds[0]),
      orderBy("Timestamp", "desc"),
      limit(10)
    );

    let userEvents = [] as UserEventModel[];
    let init = true;
    const unsubscribe = onSnapshot(
      q,
      snapShot => {
        userEvents.forEach(x => (x.changeType = ""));
        snapShot.docChanges().forEach(change => {
          const data = change.doc.data();
          const user = usersState.find(x => sameId(x.id, data.Id));
          const store = dashboardStores.find(x => sameId(x.id, data.StoreId));

          if (
            user &&
            store &&
            moment().format("YYYY-MM-DD") ===
              moment(data.Timestamp.toDate()).format("YYYY-MM-DD")
          ) {
            const event = {
              timestamp: data.Timestamp.toDate(),
              storeId: store.id,
              storeName: store.name,
              event: data.Event,
              changeType: init ? "" : change.type,
              user: {
                id: user.id,
                firstName: user.firstName,
                lastName: user.lastName,
                status: user.status,
                purchaseOverview: {
                  totalInclVat: data.PurchaseAmount,
                  latestInclVat: data.PurchaseAmount,
                  latestReceiptId: "",
                  latestTimestamp: data.TimeStamp
                }
              }
            };

            if (init) {
              userEvents.push(event as any); //First "load" we push all events
            } else {
              const eventIndex = userEvents.findIndex(
                x => x.user.id === user.id
              );
              if (eventIndex !== -1) userEvents.splice(eventIndex, 1); //Remove existing events
              if (change.type === "added" || change.type === "modified")
                userEvents.unshift(event as any); //removed events will not be added again
            }
          }
        });

        if (init) init = false;
        setUserEventsData({
          isLoading: false,
          events: userEvents.slice(0, 10)
        });
      },
      error => {
        logFirestoreError("UserEvent")(error);
        setUserEventsData({ ...userEventsData, isLoading: false, events: [] });
      }
    );

    return () => {
      unsubscribe();
    };
  }, [usersState, dashboardStores]);

  useEffect(() => {
    if (isMobilePOS) {
      setRefunds({ ...refunds, isLoading: false });
      setUserEventsData({ ...userEventsData, isLoading: false });
      setSalesKpiData({ ...salesKpiData, isLoading: false });
      setUserEventsKpiData({ ...userEventsKpiData, isLoading: false });
      return;
    }

    if (!dashboardStores.length) {
      setSalesKpiData({ ...salesKpiData, isLoading: false });
      setUserEventsKpiData({
        ...userEventsKpiData,
        isLoading: false
      });
      return;
    }

    const q = query(
      collection(firebase.firestore, "Kpi"),
      ...dateAndTenantConstraints(dashboardStores)
    );

    const unsubscribe = onSnapshot(
      q,
      snapShot => {
        let purchases = 0,
          totalSales = 0,
          visitors = 0;
        snapShot.docs.forEach(doc => {
          const data = doc.data();
          const store = dashboardStores.find(x => sameId(x.id, data.StoreId));
          if (store) {
            purchases += data.NumberOfPurchases;
            totalSales += data.TotalPurchaseAmount;
            visitors += data.NumberOfVisitors;
          }
        });
        console.info("Kpi snapshot", {
          date: moment().format("YYYY-MM-DD"),
          docs: snapShot.size,
          matchedStores: snapShot.docs.filter(d =>
            dashboardStores.some(s => sameId(s.id, d.data().StoreId))
          ).length,
          purchases,
          visitors
        });

        StatisticsApi.getAverageDatedReceiptStatistics(
          lastWeekThenStart,
          lastWeekThenEnd,
          dashboardStores.map(s => s.id)
        ).then(result => {
          let updatedSalesStats = statisticsKpiDefault.sales as any;
          if (isOk(result)) {
            updatedSalesStats = updateDatedSalesStatistics({
              isLoading: false,
              datedSalesStatistics: result.value
            } as any);
          }
          setSalesKpiData({
            ...salesKpiData,
            isLoading: false,
            purchases: {
              then: updatedSalesStats.purchases.then,
              now: purchases
            },
            totalSales: {
              then: updatedSalesStats.totalSales.then,
              now: totalSales
            }
          });

          DashboardApi.getUserEvents(
            lastWeekThenStart,
            lastWeekThenEnd,
            dashboardStores.map(s => s.id)
          ).then(result => {
            let updatedUserEventsKpiStats =
              statisticsUserEventsDefault.events as any;
            if (isOk(result)) {
              updatedUserEventsKpiStats = updateUserEventsKpiStatistics({
                isLoading: false,
                userEvents: result.value
              } as any);
            }

            setUserEventsKpiData({
              ...userEventsKpiData,
              isLoading: false,
              visitors: {
                then: updatedUserEventsKpiStats.visitors.then,
                now: visitors
              }
            });
          });
        });
      },
      error => {
        logFirestoreError("Kpi")(error);
        setSalesKpiData({ ...salesKpiData, isLoading: false });
        setUserEventsKpiData({ ...userEventsKpiData, isLoading: false });
      }
    );

    return () => {
      unsubscribe();
    };
  }, [dashboardStores]);

  return (
    <>
      <Container maxWidth={false}>
        <Box pt={{ xs: 2, sm: 1, lg: 0 }}>
          <Grid container spacing={2}>
            <Grid container item spacing={2} lg={9}>
              {!isMobilePOS && (
                <Grid item xs={12} md={6} lg={4}>
                  <VisitorsKpi
                    title={t("common.messages.visitorsToday")}
                    value={userEventsKpiData.visitors.now}
                    trendValue={calculateTrend(
                      userEventsKpiData.visitors.now,
                      userEventsKpiData.visitors.then
                    )}
                    trendText={t("common.messages.comparedToLast", {
                      weekDayName
                    })}
                    isLoading={userEventsKpiData.isLoading}
                  />
                </Grid>
              )}
              <Grid
                item
                xs={12}
                md={isMobilePOS ? 12 : 6}
                lg={
                  currentUser.tenant.tenantType.name ===
                  TenantTypeName.MobilePOS
                    ? 8
                    : 4
                }
              >
                <PurchasesKpi
                  title={t("common.messages.purchasesToday")}
                  value={
                    isMobilePOS
                      ? mobilePurchasesState.length
                      : salesKpiData.purchases.now
                  }
                  trendValue={calculateTrend(
                    salesKpiData.purchases.now,
                    salesKpiData.purchases.then
                  )}
                  showTrend={true}
                  trendText={t("common.messages.comparedToLast", {
                    weekDayName
                  })}
                  isLoading={salesKpiData.isLoading}
                />
              </Grid>
              <Grid item xs={12} md={6} lg={4}>
                <RefundsKpi
                  title={t("common.messages.refundsToday")}
                  value={refunds.now}
                  showTrend={false}
                  trendValue={calculateTrend(refunds.now, refunds.then)}
                  trendText={t("common.messages.comparedToLast", {
                    weekDayName
                  })}
                  isLoading={refunds.isLoading}
                />
              </Grid>

              {/***
               ***** Total purchase up here besides refunds on MD and down
               ***/}
              <Hidden lgUp>
                <Grid item md={6} xs={12}>
                  <TotalSalesKpi
                    title={t("common.messages.totalSales")}
                    value={
                      isMobilePOS
                        ? mobilePurchasesState
                            .map(p => p.amount)
                            .reduce(
                              (accumulator, currentValue) =>
                                accumulator + currentValue,
                              0
                            )
                        : salesKpiData.totalSales.now
                    }
                    trendValue={calculateTrend(
                      salesKpiData.totalSales.now,
                      salesKpiData.totalSales.then
                    )}
                    trendText={t("common.messages.comparedToLast", {
                      weekDayName
                    })}
                    isLoading={salesKpiData.isLoading}
                  />
                </Grid>
              </Hidden>
              <Grid container item spacing={2}>
                {/***
                 ***** No sales chart on MD and down
                 ***/}
                <Hidden mdDown>
                  <Grid container item lg={8}>
                    <Grid item xs={12} style={{ flexGrow: 1 }}>
                      <Sales
                        selectedStores={dashboardStores}
                        comparedToText={t("common.messages.last", {
                          weekDayName
                        })}
                        title={
                          <Box sx={{ pb: 2 }}>
                            <Typography
                              color="textPrimary"
                              gutterBottom
                              variant="button"
                            >
                              {t("common.messages.now")}
                            </Typography>
                          </Box>
                        }
                      />
                    </Grid>
                  </Grid>
                </Hidden>
                {/***
                 ***** No total sales and top selling products here on MD and down.
                 ***** Move these two to other locations to fill up gaps
                 ***/}
                <Hidden mdDown>
                  <Grid
                    container
                    item
                    xs={12}
                    lg={4}
                    alignContent={"flex-start"}
                  >
                    <Grid
                      item
                      xs={12}
                      md={6}
                      lg={12}
                      style={{ height: "100%" }}
                    >
                      <Box
                        display={"flex"}
                        flexDirection={"column"}
                        justifyContent={"stretch"}
                        height={"100%"}
                      >
                        <Box className={classes.totalSalesKpi}>
                          <TotalSalesKpi
                            title={t("common.messages.totalSales")}
                            value={
                              isMobilePOS
                                ? mobilePurchasesState
                                    .map(p => p.amount)
                                    .reduce(
                                      (accumulator, currentValue) =>
                                        accumulator + currentValue,
                                      0
                                    )
                                : salesKpiData.totalSales.now
                            }
                            trendValue={calculateTrend(
                              salesKpiData.totalSales.now,
                              salesKpiData.totalSales.then
                            )}
                            trendText={t("common.messages.comparedToLast", {
                              weekDayName
                            })}
                            isLoading={salesKpiData.isLoading}
                          />
                        </Box>
                        <TopSellingProducts />
                      </Box>
                    </Grid>
                  </Grid>
                </Hidden>
              </Grid>
            </Grid>
            <Grid item xs={12} md={12} lg={3} style={{ marginLeft: "auto" }}>
              <Paper style={{ height: "100%" }}>
                {currentUser.tenant.tenantType.name ===
                TenantTypeName.MobilePOS ? (
                  <MobilePOSPurchases
                    isLoading={loading}
                    purchases={mobilePurchasesState}
                  />
                ) : (
                  <UserEvents {...userEventsData} />
                )}
              </Paper>
            </Grid>
            {/***
             ***** Show top selling products here on MD and down.
             ***/}
            <Hidden lgUp>
              <Grid item xs={12}>
                <TopSellingProducts />
              </Grid>
            </Hidden>
          </Grid>
        </Box>
        <Outlet />
      </Container>

      <OnBoardingDialog />
    </>
  );
};
